/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Grid } from '@coursera/cds-core';

import { numberOfRelatedCoursesForDomains } from 'bundles/browse/components/Domain/constants/domainData';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';

import _t from 'i18n!nls/browse';

const TOPIC_CARD_IMAGE_WIDTH = 68;
const TOPIC_CARD_IMAGE_HEIGHT = 74;

type Props = {
  id: string;
  slug: string | null;
  name: string;
  imageUrl: string;
};

const style = css`
  color: #1f1f1f;
  text-decoration: none;

  &:hover {
    color: #929599;
    text-decoration: none;
  }

  .topic-card-container {
    height: 74px;
    width: 200px;
    box-shadow: 0 2px 4px rgb(0 0 0 / 30%);
    border-radius: 2px;
    transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms;
    overflow: hidden;
    cursor: pointer;

    &:hover {
      box-shadow: rgb(31 31 31 / 16%) 0 3px 10px, rgb(31 31 31 / 23%) 0 3px 10px;
    }

    @media (max-width: 599px) {
      width: 140px;
      height: 88px;
    }
  }

  .topic-card-image-container {
    @media (max-width: 599px) {
      display: none;
    }
  }

  .topic-card-label-container {
    color: #1f1f1f;
    padding: 0 10px;

    .topic-name {
      /* h4bold */
      font-size: 0.875rem;
      line-height: 1.25rem;
      font-family: 'Source Sans Pro', Arial, sans-serif;
      font-weight: 700;
    }

    .topic-number {
      color: #636363;

      /* body2 */
      font-size: 0.875rem;
      line-height: 1.25rem;
      font-family: 'Source Sans Pro', Arial, sans-serif;
      font-weight: 400;
    }
  }
`;

const TopicCard = ({ id, name, slug, imageUrl }: Props) => {
  const numberOfCourses = numberOfRelatedCoursesForDomains[slug || id];

  return (
    <TrackedA className="rc-TopicCard" css={style} href={`/browse/${id}`} trackingName="topic_card">
      <Grid className="topic-card-container" container>
        <Grid className="topic-card-image-container" item xs={4}>
          <LazyImgix
            src={imageUrl}
            alt=""
            maxWidth={TOPIC_CARD_IMAGE_WIDTH}
            maxHeight={TOPIC_CARD_IMAGE_HEIGHT}
            imgParams={{ fit: 'crop', auto: 'format,compress' }}
            disableDpr3SrcSet
          />
        </Grid>
        <Grid className="topic-card-label-container" item container alignItems="center" xs={12} sm={8}>
          <Grid item xs={12}>
            <div className="topic-name">{name}</div>
            {numberOfCourses && (
              <span className="topic-number">{_t('#{numberOfCourses} courses', { numberOfCourses })}</span>
            )}
          </Grid>
        </Grid>
      </Grid>
    </TrackedA>
  );
};

export default TopicCard;
